import React, { useEffect, useMemo, useState } from 'react';
import KPIReviewRow from './KPIReviewRow';
import './reviews-table.css';

/**
 * Props:
 * - rows: [{ kpi: { id, name, weight, threshold, target, stretch, is_percentage, unit, key_performance_area_id, kpa?: { id, name, weight? }, kpa_name? }, review }]
 * - ratings: [{ id, name, code, description }]
 * - reviewTypeId: number
 * - onSaveRow: ({ mode:'create'|'update', reviewId?, payload }) => Promise<void>
 * - defaultExpanded?: 'all' | 'none'
 */
export default function KPIReviewTable({
                                           rows,
                                           ratings,
                                           reviewTypeId,
                                           onSaveRow,
                                           defaultExpanded = 'all',
                                       }) {
    // ---- Robust KPA grouping ----
    const sections = useMemo(() => {
        const map = new Map();
        for (const r of rows || []) {
            const kpi = r?.kpi || {};
            const kpaId =
                kpi.key_performance_area_id ??
                kpi.kpa?.id ??
                // last resort: make a unique group per KPI so it never collapses into one
                `kpi-${kpi.id}`;

            // Try to name the KPA
            const kpaName =
                kpi.kpa?.name ??
                kpi.kpa_name ??
                // fallback friendly label
                `KPA ${String(kpaId).toString()}`;

            const kpaWeight =
                kpi.kpa?.weight != null ? Number(kpi.kpa.weight) : undefined;

            if (!map.has(kpaId)) {
                map.set(kpaId, { kpaId, kpaName, kpaWeight, items: [] });
            }
            map.get(kpaId).items.push(r);
        }

        // Sort by numeric ID, not name
        return Array.from(map.values());

    }, [rows]);

    // Collapsible state
    const [openKpas, setOpenKpas] = useState(new Set());
    useEffect(() => {
        const init = new Set();
        if (defaultExpanded === 'all') sections.forEach(s => init.add(String(s.kpaId)));
        setOpenKpas(init);
    }, [sections, defaultExpanded]);

    const isOpen = (kpaId) => openKpas.has(String(kpaId));
    const toggle = (kpaId) =>
        setOpenKpas(prev => {
            const s = new Set(prev);
            const k = String(kpaId);
            s.has(k) ? s.delete(k) : s.add(k);
            return s;
        });
    const expandAll = () => setOpenKpas(new Set(sections.map(s => String(s.kpaId))));
    const collapseAll = () => setOpenKpas(new Set());
    const Caret = ({ open }) => <span className="me-2">{open ? '▾' : '▸'}</span>;

    // Sum of KPA weights (when provided on the sections)
    const totalKpaWeight = useMemo(() => {
        let sum = 0;
        sections.forEach(s => {
            if (s.kpaWeight != null) sum += Number(s.kpaWeight);
        });
        return sum;
    }, [sections]);

    return (
        <div className="reviews-table">
            {/* Header controls */}
            <div className="d-flex justify-content-end gap-2 mb-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={expandAll}>Expand all</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>Collapse all</button>
            </div>

            <table>
                <thead>
                {/* Group bands */}
                <tr className="rt-head-top">
                    <th rowSpan={2} className="rt-counter">#</th>
                    <th rowSpan={2} className="rt-name">Performance Review Discussion and Ratings</th>
                    <th colSpan={3}>Acceptable / Expected / Exceeded Output</th>
                    <th rowSpan={2}>Weight</th>
                    <th rowSpan={2}>Actuals</th>
                    <th colSpan={2}>Employee Evaluation</th>
                    <th colSpan={2}>Employer Evaluation</th>
                    <th rowSpan={2} className="rt-actions">Actions</th>
                </tr>
                {/* Column labels */}
                <tr className="rt-head-bottom">
                    <th>Threshold</th>
                    <th>Target</th>
                    <th>Stretch</th>
                    <th>Rating (EE)</th>
                    <th>Description</th>
                    <th>Rating (ER)</th>
                    <th>Description</th>
                </tr>
                </thead>

                <tbody>
                {sections.length === 0 && (
                    <tr><td colSpan={12} className="text-center text-muted py-4">No KPIs found.</td></tr>
                )}

                {sections.map((sec, kpaIndex) => {
                    const open = isOpen(sec.kpaId);
                    const kpaCounter = `KPA ${String(kpaIndex + 1).padStart(2, '0')}`;
                    return (
                        <React.Fragment key={String(sec.kpaId)}>
                            {/* KPA header row */}
                            <tr className="rt-kpa-row">
                                <td className="rt-counter">{kpaCounter}</td>
                                <td className="rt-name">
                                    <button
                                        className="rt-kpa-toggle"
                                        onClick={() => toggle(sec.kpaId)}
                                        title={open ? 'Collapse' : 'Expand'}
                                    >
                                        <Caret open={open} />
                                        {sec.kpaName}
                                    </button>
                                </td>
                                <td className="rt-num">—</td>
                                <td className="rt-num">—</td>
                                <td className="rt-num">—</td>
                                <td className="rt-num">{sec.kpaWeight ?? '—'}</td>
                                <td className="rt-num">—</td>
                                <td colSpan={4}></td>
                                <td className="rt-actions"></td>
                            </tr>

                            {/* KPI rows in this KPA */}
                            {open && (sec.items || []).map((row, idx) => (
                                <KPIReviewRow
                                    key={row.kpi.id}
                                    kpi={row.kpi}
                                    review={row.review}
                                    ratings={ratings}
                                    reviewTypeId={reviewTypeId}
                                    onSave={onSaveRow}
                                    counterText={`KPI ${String(idx + 1).padStart(2, '0')}`}
                                />
                            ))}
                        </React.Fragment>
                    );
                })}
                </tbody>

                <tfoot>
                <tr>
                    <td className="rt-counter">Total</td>
                    <td className="rt-name"></td>
                    <td className="rt-num">0.0</td>
                    <td className="rt-num">0.0</td>
                    <td className="rt-num">0.0</td>
                    <td className="rt-num">{totalKpaWeight}</td>
                    <td className="rt-num">0.0</td>
                    <td colSpan={5}></td>
                </tr>
                </tfoot>
            </table>
        </div>
    );
}